
import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';

// --- Types ---
type AppView = 'hub' | 'email' | 'objection' | 'assessment';

interface EmailGrade {
  grade: string;
  scores: { subject: number; personalization: number; clarity: number; cta: number };
  feedback: string[];
  improvedVersion: string;
}

interface ObjectionResponse {
  strategies: {
    type: string;
    method: string;
    description: string;
    script: string;
  }[];
}

interface ReadinessReport {
  score: number;
  categories: { name: string; score: number }[];
  strengths: string[];
  weaknesses: string[];
  executiveSummary: string;
  nextSteps: string[];
}

// --- Gemini API Helper ---
// Calls our secure Cloudflare Worker proxy instead of exposing API key in browser
async function callGemini(model: string, prompt: string): Promise<string> {
  const response = await fetch('/api/gemini', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      contents: prompt,
      config: {
        responseMimeType: 'application/json',
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'API request failed');
  }

  const data = await response.json();
  return data.text;
}

// --- Components ---

const Header = ({ onBack, showBack }: { onBack: () => void, showBack: boolean }) => (
  showBack ? (
    <div className="p-6 flex items-center border-b border-slate-800/50">
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors"
      >
        <i data-lucide="arrow-left" className="w-5 h-5"></i>
        <span className="text-sm font-medium">Back to Suite</span>
      </button>
    </div>
  ) : null
);

const ColdEmailApp = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EmailGrade | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyze = async () => {
    if (!email) return;
    setLoading(true);
    setCopied(false);
    setError(null);
    
    try {
      const prompt = `Analyze this cold email and provide improvements. Return a JSON object with these exact fields:
- grade: A letter grade (A+, A, B+, B, C, D, F)
- scores: An object with subject (0-100), personalization (0-100), clarity (0-100), cta (0-100)
- feedback: An array of 3-5 specific improvement suggestions
- improvedVersion: A rewritten version of the email that addresses the feedback

Email to analyze:
"${email}"

Respond with valid JSON only, no markdown.`;

      const responseText = await callGemini('gemini-2.0-flash-exp', prompt);
      const cleaned = responseText.replace(/```json\n?|```\n?/g, '').trim();
      setResult(JSON.parse(cleaned));
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(result.improvedVersion);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8 text-center">
        <h2 className="text-4xl font-bold mb-4 gradient-text">Cold Email Grader</h2>
        <p className="text-slate-400">Optimize your outreach with data-driven linguistic analysis.</p>
      </div>

      <div className="glass p-6 rounded-3xl mb-8 border border-slate-700/50">
        <textarea 
          className="w-full bg-slate-900/50 border border-slate-700 rounded-2xl p-6 text-white focus:ring-2 focus:ring-indigo-500 outline-none min-h-[220px] transition-all"
          placeholder="Paste your cold email here (including subject line)..."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button 
          disabled={loading}
          onClick={analyze}
          className="mt-6 w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-600/30"
        >
          {loading ? <i data-lucide="refresh-cw" className="animate-spin w-5 h-5"></i> : <i data-lucide="zap" className="w-5 h-5"></i>}
          {loading ? 'Crunching Metrics...' : 'Grade & Optimize Outreach'}
        </button>
        
        {error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="space-y-6 animate-in zoom-in-95 duration-500">
          <div className="grid md:grid-cols-4 gap-4">
            {Object.entries(result.scores).map(([key, val]) => (
              <div key={key} className="glass p-4 rounded-2xl text-center border-slate-800">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">{key}</span>
                <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden mb-2">
                  <div className={`h-full bg-indigo-500 transition-all duration-1000`} style={{ width: `${val}%` }}></div>
                </div>
                <span className="text-lg font-bold text-white">{val}%</span>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass p-8 rounded-3xl text-center border-indigo-500/30 border bg-gradient-to-br from-indigo-500/5 to-transparent">
              <span className="text-xs uppercase tracking-widest text-indigo-400 font-bold block mb-2">Alpha Grade</span>
              <span className="text-8xl font-black text-white drop-shadow-lg">{result.grade}</span>
            </div>
            <div className="md:col-span-2 glass p-8 rounded-3xl border-slate-800">
              <span className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-6">Critical Feedback</span>
              <ul className="space-y-4">
                {result.feedback.map((f, i) => (
                  <li key={i} className="flex gap-4 text-slate-300 text-sm items-start">
                    <div className="p-1 bg-indigo-500/20 rounded text-indigo-400">
                      <i data-lucide="check" className="w-3 h-3"></i>
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="glass p-8 rounded-3xl border-emerald-500/20 border relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4">
               <button 
                onClick={copyToClipboard}
                className="flex items-center gap-2 text-xs font-bold px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg transition-all border border-emerald-500/20"
               >
                 <i data-lucide={copied ? "check" : "copy"} className="w-4 h-4"></i>
                 {copied ? 'Copied!' : 'Copy Script'}
               </button>
            </div>
            <span className="text-xs uppercase tracking-widest text-emerald-500 font-bold block mb-6">Optimized Pro Version</span>
            <div className="bg-slate-950/50 p-8 rounded-2xl border border-slate-800 text-slate-200 whitespace-pre-wrap font-serif leading-relaxed text-lg">
              {result.improvedVersion}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ObjectionCoachApp = () => {
  const [objection, setObjection] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ObjectionResponse | null>(null);
  const [type, setType] = useState('General');
  const [error, setError] = useState<string | null>(null);

  const types = ['Price', 'Competitor', 'Timing', 'Authority', 'Technical'];

  const handleConsult = async () => {
    if (!objection) return;
    setLoading(true);
    setError(null);
    
    try {
      const prompt = `CONTEXT: The prospect raised a ${type} objection. 
OBJECTION: "${objection}"

Provide 3 elite response strategies. Return a JSON object with this exact structure:
{
  "strategies": [
    {
      "type": "Strategy type name",
      "method": "Method name (e.g., Feel-Felt-Found, Empathy Pivot, Discovery Loop)",
      "description": "2-3 sentence explanation of this approach",
      "script": "The exact words to say"
    }
  ]
}

Respond with valid JSON only, no markdown.`;

      const responseText = await callGemini('gemini-2.0-flash-exp', prompt);
      const cleaned = responseText.replace(/```json\n?|```\n?/g, '').trim();
      setResult(JSON.parse(cleaned));
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8 animate-in fade-in duration-500">
      <div className="mb-8 text-center">
        <h2 className="text-4xl font-bold mb-4 text-cyan-400">Objection Response Coach</h2>
        <p className="text-slate-400">Master high-stakes negotiation with strategic conversational pivots.</p>
      </div>

      <div className="glass p-8 rounded-[2.5rem] mb-12 border-slate-800 shadow-2xl">
        <div className="flex flex-wrap gap-2 mb-6">
          {types.map(t => (
            <button 
              key={t}
              onClick={() => setType(t)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${type === t ? 'bg-cyan-500 text-slate-950 border-cyan-400 shadow-lg shadow-cyan-500/20' : 'bg-slate-900 text-slate-500 border-slate-700 hover:border-slate-500'}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="relative group">
          <input 
            type="text"
            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-6 pr-44 text-white focus:ring-2 focus:ring-cyan-500 outline-none text-xl transition-all group-hover:border-slate-700"
            placeholder='e.g., "The budget is locked until Q4."'
            value={objection}
            onChange={(e) => setObjection(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleConsult()}
          />
          <button 
            disabled={loading}
            onClick={handleConsult}
            className="absolute right-3 top-3 bottom-3 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 px-8 rounded-xl font-bold transition-all flex items-center gap-2"
          >
            {loading ? <i data-lucide="loader" className="animate-spin w-5 h-5"></i> : <i data-lucide="send" className="w-5 h-5"></i>}
            Analyze
          </button>
        </div>
        
        {error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="grid md:grid-cols-3 gap-6">
          {result.strategies.map((s, i) => (
            <div key={i} className="glass p-8 rounded-[2.5rem] border-slate-800 border hover:border-cyan-500/40 transition-all group hover:scale-[1.02] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">{s.type}</span>
                <span className="bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded text-[10px] font-bold">{s.method}</span>
              </div>
              <p className="text-slate-400 text-sm mb-8 leading-relaxed flex-grow">{s.description}</p>
              <div className="bg-slate-950 p-5 rounded-2xl text-cyan-50 font-medium italic border border-cyan-500/10 group-hover:bg-cyan-500/5 transition-colors text-sm relative">
                <i data-lucide="quote" className="w-8 h-8 absolute -top-4 -left-2 text-cyan-500/10 rotate-12"></i>
                "{s.script}"
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SalesAssessmentApp = () => {
  const [pitch, setPitch] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReadinessReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runAssessment = async () => {
    if (!pitch) return;
    setLoading(true);
    setError(null);
    
    try {
      const prompt = `Act as a world-class sales consultant. Audit this pitch/strategy and provide a comprehensive assessment.

Pitch/Strategy to analyze:
"${pitch}"

Return a JSON object with this exact structure:
{
  "score": 75,
  "executiveSummary": "2-3 sentence overall assessment",
  "categories": [
    {"name": "Empathy", "score": 80},
    {"name": "Clarity", "score": 70},
    {"name": "Persistence", "score": 65},
    {"name": "Logic", "score": 85}
  ],
  "strengths": ["Strength 1", "Strength 2", "Strength 3"],
  "weaknesses": ["Weakness 1", "Weakness 2", "Weakness 3"],
  "nextSteps": ["Action item 1", "Action item 2", "Action item 3"]
}

Respond with valid JSON only, no markdown.`;

      const responseText = await callGemini('gemini-2.0-flash-exp', prompt);
      const cleaned = responseText.replace(/```json\n?|```\n?/g, '').trim();
      setReport(JSON.parse(cleaned));
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-8 animate-in fade-in duration-500">
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-bold mb-4 text-violet-400">Sales Readiness Audit</h2>
        <p className="text-slate-400">Deep-dive diagnostic for your sales framework and pitch effectiveness.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-5 glass p-8 rounded-[2.5rem] border-slate-800 sticky top-32">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
            <div className="p-2 bg-violet-500/20 rounded-lg text-violet-400">
              <i data-lucide="target" className="w-5 h-5"></i>
            </div>
            Pitch Repository
          </h3>
          <textarea 
            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-6 text-white focus:ring-2 focus:ring-violet-500 outline-none min-h-[350px] text-sm leading-relaxed mb-6"
            placeholder="Outline your pitch, common discovery questions, and your closing framework..."
            value={pitch}
            onChange={(e) => setPitch(e.target.value)}
          />
          <button 
            disabled={loading}
            onClick={runAssessment}
            className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-violet-600/30 flex items-center justify-center gap-2"
          >
            {loading ? <i data-lucide="settings" className="animate-spin w-5 h-5"></i> : <i data-lucide="activity" className="w-5 h-5"></i>}
            {loading ? 'Analyzing Strategy...' : 'Start Diagnostic'}
          </button>
          
          {error && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          {!report && !loading && (
            <div className="glass p-20 rounded-[2.5rem] border-dashed border-2 border-slate-800 flex flex-col items-center justify-center text-slate-500">
              <i data-lucide="bar-chart-2" className="w-16 h-16 mb-6 opacity-20"></i>
              <p className="text-center max-w-xs">Enter your strategy data to generate your Readiness Scorecard.</p>
            </div>
          )}
          
          {loading && (
            <div className="glass p-12 rounded-[2.5rem] animate-pulse space-y-8">
              <div className="h-40 bg-slate-800/50 rounded-3xl"></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="h-32 bg-slate-800/50 rounded-2xl"></div>
                <div className="h-32 bg-slate-800/50 rounded-2xl"></div>
              </div>
              <div className="h-24 bg-slate-800/50 rounded-2xl"></div>
            </div>
          )}

          {report && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-700">
              <div className="glass p-10 rounded-[2.5rem] border-violet-500/30 border bg-gradient-to-br from-violet-500/5 to-transparent flex flex-col md:flex-row items-center gap-8">
                <div className="relative w-32 h-32 flex items-center justify-center shrink-0">
                   <svg className="w-full h-full -rotate-90">
                     <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-800" />
                     <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-violet-500" strokeDasharray={364} strokeDashoffset={364 - (364 * report.score / 100)} strokeLinecap="round" />
                   </svg>
                   <span className="absolute text-3xl font-black text-white">{report.score}%</span>
                </div>
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-violet-400 font-bold mb-2">Executive Summary</h4>
                  <p className="text-slate-300 text-sm leading-relaxed">{report.executiveSummary}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {report.categories.map((c, i) => (
                  <div key={i} className="glass p-5 rounded-2xl border-slate-800">
                    <span className="text-[10px] font-bold text-slate-500 uppercase block mb-3">{c.name}</span>
                    <div className="text-xl font-bold text-white mb-2">{c.score}%</div>
                    <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500" style={{ width: `${c.score}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="glass p-8 rounded-[2rem] border-emerald-500/10 border">
                  <h4 className="text-emerald-400 font-bold text-xs mb-4 uppercase tracking-wider flex items-center gap-2">
                    <i data-lucide="check-circle-2" className="w-4 h-4"></i> Competitive Edge
                  </h4>
                  <ul className="text-sm space-y-3 text-slate-300">
                    {report.strengths.map((s, i) => <li key={i} className="flex gap-2"><span>•</span> {s}</li>)}
                  </ul>
                </div>
                <div className="glass p-8 rounded-[2rem] border-red-500/10 border">
                  <h4 className="text-red-400 font-bold text-xs mb-4 uppercase tracking-wider flex items-center gap-2">
                    <i data-lucide="alert-triangle" className="w-4 h-4"></i> Critical Gaps
                  </h4>
                  <ul className="text-sm space-y-3 text-slate-300">
                    {report.weaknesses.map((w, i) => <li key={i} className="flex gap-2"><span>•</span> {w}</li>)}
                  </ul>
                </div>
              </div>

              <div className="glass p-8 rounded-[2rem] border-indigo-500/20 border">
                <h4 className="text-indigo-400 font-bold text-xs mb-6 uppercase tracking-wider">Growth Roadmap</h4>
                <div className="grid gap-4">
                  {report.nextSteps.map((r, i) => (
                    <div key={i} className="flex items-center gap-4 text-sm text-slate-200 bg-slate-950/60 p-5 rounded-2xl border border-slate-800 hover:border-indigo-500/30 transition-all">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-bold border border-indigo-500/20">{i+1}</div>
                      {r}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Hub = ({ onNavigate }: { onNavigate: (v: AppView) => void }) => {
  const cards = [
    {
      id: 'email' as AppView,
      title: 'Outreach Optimizer',
      desc: 'Harness linguistic AI to grade and rewrite cold emails for 40% higher open rates.',
      icon: 'mail',
      color: 'from-indigo-600 to-blue-500',
      img: 'https://images.unsplash.com/photo-1596524430615-b46475ddff6e?auto=format&fit=crop&q=80&w=800',
      tag: 'Engagement'
    },
    {
      id: 'objection' as AppView,
      title: 'Objection Response',
      desc: 'Real-time pivot strategies for the toughest rejections. Turn "No" into "Not Yet".',
      icon: 'shield-alert',
      color: 'from-cyan-600 to-teal-500',
      img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800',
      tag: 'Negotiation'
    },
    {
      id: 'assessment' as AppView,
      title: 'Readiness Audit',
      desc: 'Comprehensive diagnostic report on your sales pitch, logic, and market readiness.',
      icon: 'bar-chart-3',
      color: 'from-violet-600 to-purple-500',
      img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
      tag: 'Strategy'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto p-8 animate-in fade-in duration-1000">
      <div className="text-center mb-20 relative">
        <div className="absolute inset-0 -top-20 bg-indigo-500/10 blur-[120px] rounded-full"></div>
        <h2 className="text-6xl md:text-8xl font-black mb-8 tracking-tighter relative">
          Sales <span className="gradient-text">Enablement</span>
        </h2>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto font-light leading-relaxed">
          The SoloFrameHub Sales Suite equips high-performance revenue teams with precision-engineered AI intelligence.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-20">
        {cards.map((card) => (
          <div 
            key={card.id}
            onClick={() => onNavigate(card.id)}
            className="group glass rounded-[3rem] overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-700 border-2 border-transparent hover:border-slate-700/50 shadow-2xl relative"
          >
            <div className="h-64 overflow-hidden relative">
              <img src={card.img} alt={card.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 opacity-40 grayscale group-hover:grayscale-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest text-white border border-white/20">{card.tag}</span>
              </div>
              <div className="absolute bottom-8 left-8 flex items-center gap-4">
                <div className={`p-4 rounded-2xl bg-gradient-to-br ${card.color} shadow-2xl shadow-indigo-500/20`}>
                  <i data-lucide={card.icon} className="w-6 h-6 text-white"></i>
                </div>
              </div>
            </div>
            <div className="p-10">
              <h3 className="text-2xl font-bold mb-4 group-hover:text-indigo-400 transition-colors">{card.title}</h3>
              <p className="text-slate-400 font-light leading-relaxed text-sm mb-10">{card.desc}</p>
              <div className="flex items-center text-xs font-black uppercase tracking-[0.2em] text-indigo-400 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                Enter Dashboard <i data-lucide="chevron-right" className="ml-2 w-4 h-4"></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 opacity-30 grayscale hover:grayscale-0 transition-all duration-700 border-t border-slate-900 pt-16">
        <div className="text-center font-black tracking-widest">ENTERPRISE READY</div>
        <div className="text-center font-black tracking-widest">GEMINI 2.0</div>
        <div className="text-center font-black tracking-widest">SECURE PROXY</div>
        <div className="text-center font-black tracking-widest">CLOUDFLARE EDGE</div>
      </div>
    </div>
  );
};

const App = () => {
  const [view, setView] = useState<AppView>('hub');

  useEffect(() => {
    // Refresh icons on state change and mount
    const updateIcons = () => {
       if ((window as any).lucide) {
        (window as any).lucide.createIcons();
      }
    }
    updateIcons();
    const timer = setTimeout(updateIcons, 100); // Buffer for rendering
    return () => clearTimeout(timer);
  }, [view]);

  return (
    <>
      <Header showBack={view !== 'hub'} onBack={() => setView('hub')} />
      <main className="relative">
        {view === 'hub' && <Hub onNavigate={setView} />}
        {view === 'email' && <ColdEmailApp />}
        {view === 'objection' && <ObjectionCoachApp />}
        {view === 'assessment' && <SalesAssessmentApp />}
      </main>
    </>
  );
};

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<App />);
